import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Table,
  InputGroup,
} from "react-bootstrap";
import {
  FaUpload,
  FaRocket,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileAlt,
  FaDownload,
  FaLayerGroup,
  FaLightbulb,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import Layout from "./Layout";
import axios from "axios";
import "../assets/css/SpCategoryCampaigns.css";

const SpCategoryCampaigns = () => {
  // API Configuration
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

  // Estados para configuración
  const [siteType, setSiteType] = useState("AMAZON_AND_BEYOND");

  // Estados para archivos
  const [productsFile, setProductsFile] = useState(null);

  // Estados para categorías
  const [categories, setCategories] = useState([
    { category: "", subcategory: "", categoryId: "" },
  ]);
  const [suggestedCategories, setSuggestedCategories] = useState([]);

  // Estados de configuración de campaña
  const [defaultBid, setDefaultBid] = useState(0.75);
  const [defaultBudget, setDefaultBudget] = useState(10);
  const [campaignState, setCampaignState] = useState("PAUSED");
  const [biddingStrategy, setBiddingStrategy] = useState("SALES_DOWN_ONLY");

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [suggestingCategories, setSuggestingCategories] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [creationComplete, setCreationComplete] = useState(false);

  // Bulk actions states
  const [bulkAction, setBulkAction] = useState("");
  const [bulkBudget, setBulkBudget] = useState("");
  const [bulkBid, setBulkBid] = useState("");

  // Manejar carga de archivo de productos
  const handleProductsFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setProductsFile(file);
      setError(null);
    } else {
      setError("Por favor selecciona un archivo CSV válido");
    }
  };

  // Manejar cambio en categorías
  const handleCategoryChange = (index, field, value) => {
    const updatedCategories = [...categories];
    updatedCategories[index][field] = value;
    setCategories(updatedCategories);
  };

  // Agregar nueva categoría
  const addCategory = () => {
    setCategories([
      ...categories,
      { category: "", subcategory: "", categoryId: "" },
    ]);
  };

  // Eliminar categoría
  const removeCategory = (index) => {
    if (categories.length > 1) {
      const updatedCategories = categories.filter((_, i) => i !== index);
      setCategories(updatedCategories);
    }
  };

  // Sugerir categorías basado en ASINs
  const handleSuggestCategories = async () => {
    if (!productsFile) {
      setError("Primero sube el archivo de productos para sugerir categorías");
      return;
    }

    setSuggestingCategories(true);
    setError(null);

    try {
      const profileId = localStorage.getItem("selectedAccountValue");

      if (!profileId) {
        setError("Por favor selecciona una cuenta en el selector de arriba");
        setSuggestingCategories(false);
        return;
      }

      const formData = new FormData();
      formData.append("products_file", productsFile);
      formData.append("profileId", profileId);

      const response = await axios.post(
        `${API_URL}/sp-category-campaigns/suggest-categories`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        setSuggestedCategories(response.data.categories || []);

        // Auto-fill first category if suggestions available
        if (response.data.categories && response.data.categories.length > 0) {
          const suggested = response.data.categories.slice(0, 3).map((cat) => ({
            category: cat.name || cat.category,
            subcategory: cat.subcategory || "",
            categoryId: cat.id || cat.categoryId || "",
          }));
          setCategories(suggested.length > 0 ? suggested : categories);
        }

        setResult({
          success: true,
          message: `${response.data.categories.length} categorías sugeridas`,
        });
      } else {
        setError(response.data.error || "Error al sugerir categorías");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(err.response?.data?.error || "Error al sugerir categorías");
    } finally {
      setSuggestingCategories(false);
    }
  };

  // Seleccionar categoría sugerida
  const selectSuggestedCategory = (suggested) => {
    const newCategory = {
      category: suggested.name || suggested.category,
      subcategory: suggested.subcategory || "",
      categoryId: suggested.id || suggested.categoryId || "",
    };

    // Check if already exists
    const exists = categories.some(
      (c) =>
        c.category === newCategory.category &&
        c.subcategory === newCategory.subcategory,
    );

    if (!exists) {
      if (categories.length === 1 && !categories[0].category) {
        setCategories([newCategory]);
      } else {
        setCategories([...categories, newCategory]);
      }
    }
  };

  // Generar campañas de categoría
  const handleGenerateCampaigns = async () => {
    if (!productsFile) {
      setError("Debes subir el archivo de productos (ASIN, SKU)");
      return;
    }

    const validCategories = categories.filter((c) => c.category.trim());
    if (validCategories.length === 0) {
      setError("Debes ingresar al menos una categoría");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("products_file", productsFile);
      formData.append("site_type", siteType);
      formData.append("default_bid", defaultBid);
      formData.append("default_budget", defaultBudget);
      formData.append("bidding_strategy", biddingStrategy);
      formData.append("categories", JSON.stringify(validCategories));

      const response = await axios.post(
        `${API_URL}/sp-category-campaigns/generate`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        const generatedCampaigns = response.data.campaigns || [];

        // Agregar campo 'selected' a cada campaña
        const campaignsWithSelection = generatedCampaigns.map((campaign) => ({
          ...campaign,
          selected: true,
        }));

        setCampaigns(campaignsWithSelection);

        setResult({
          success: true,
          message: `${generatedCampaigns.length} campañas de categoría generadas. Selecciona cuáles crear en Amazon.`,
          count: generatedCampaigns.length,
        });
      } else {
        setError(response.data.error || "Error al generar campañas");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response?.data?.error || err.message || "Error al generar campañas",
      );
    } finally {
      setLoading(false);
    }
  };

  // Crear campañas en Amazon
  const handleCreateInAmazon = async () => {
    const selectedCampaigns = campaigns.filter((c) => c.selected);

    if (selectedCampaigns.length === 0) {
      setError("Debes seleccionar al menos una campaña para crear en Amazon");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const profileId = localStorage.getItem("selectedAccountValue");

      if (!profileId) {
        setError(
          "Por favor selecciona una cuenta en el selector de arriba antes de crear campañas",
        );
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/sp-category-campaigns/create-in-amazon`,
        {
          profileId,
          campaigns: selectedCampaigns,
          campaignState,
          siteType,
          biddingStrategy,
        },
      );

      if (response.data.success) {
        const amazonResults = response.data.results || [];
        const errors = response.data.errors || [];

        setResult({
          success: true,
          message: response.data.message,
          count: response.data.campaigns_created,
          amazonResults: amazonResults,
          errors: errors,
        });

        const campaignsForTable = amazonResults.map((result) => ({
          "Campaign Name": result.campaignName,
          "Campaign ID": result.campaignId,
          "Ad Group ID": result.adGroupId,
          Category: result.category,
          Subcategory: result.subcategory || "-",
          Status: "Created in Amazon",
        }));

        setCampaigns(campaignsForTable);
        setCreationComplete(true);
      } else {
        setError(response.data.error || "Error al crear campañas en Amazon");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Error al crear campañas en Amazon",
      );
    } finally {
      setLoading(false);
    }
  };

  // Toggle selección
  const toggleCampaignSelection = (index) => {
    const updatedCampaigns = [...campaigns];
    updatedCampaigns[index].selected = !updatedCampaigns[index].selected;
    setCampaigns(updatedCampaigns);
  };

  const selectAllCampaigns = () => {
    const updatedCampaigns = campaigns.map((c) => ({ ...c, selected: true }));
    setCampaigns(updatedCampaigns);
  };

  const deselectAllCampaigns = () => {
    const updatedCampaigns = campaigns.map((c) => ({ ...c, selected: false }));
    setCampaigns(updatedCampaigns);
  };

  // Aplicar acción masiva
  const applyBulkAction = () => {
    if (!bulkAction) {
      setError("Selecciona una acción masiva");
      return;
    }

    const selectedCount = campaigns.filter((c) => c.selected).length;
    if (selectedCount === 0) {
      setError("Selecciona al menos una campaña");
      return;
    }

    const updatedCampaigns = campaigns.map((campaign) => {
      if (!campaign.selected) return campaign;

      const updated = { ...campaign };

      switch (bulkAction) {
        case "budget":
          if (bulkBudget && parseFloat(bulkBudget) > 0) {
            updated.Budget = parseFloat(bulkBudget);
          }
          break;
        case "bid":
          if (bulkBid && parseFloat(bulkBid) > 0) {
            updated.Bid = parseFloat(bulkBid);
          }
          break;
        default:
          break;
      }

      return updated;
    });

    setCampaigns(updatedCampaigns);
    setBulkAction("");
    setBulkBudget("");
    setBulkBid("");
    setError(null);
    setResult({
      success: true,
      message: `Acción aplicada a ${selectedCount} campaña(s)`,
      count: selectedCount,
    });
  };

  // Descargar CSV
  const handleDownloadCampaigns = () => {
    if (campaigns.length === 0) return;

    const headers = Object.keys(campaigns[0]).filter((h) => h !== "selected");
    const csvContent = [
      headers.join(","),
      ...campaigns.map((campaign) =>
        headers
          .map((header) => {
            const value = campaign[header];
            if (typeof value === "string" && value.includes(",")) {
              return `"${value}"`;
            }
            return value;
          })
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `sp_category_campaigns_${Date.now()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reiniciar
  const handleReset = () => {
    setProductsFile(null);
    setCategories([{ category: "", subcategory: "", categoryId: "" }]);
    setCampaigns([]);
    setResult(null);
    setError(null);
    setCreationComplete(false);
    setSuggestedCategories([]);
  };

  return (
    <Layout>
      <Container fluid className="sp-category-campaigns-page py-4">
        <Row className="mb-4">
          <Col>
            <h2 className="text-white mb-3">
              <FaLayerGroup className="me-2" />
              Category Campaigns - Sponsored Products
            </h2>
            <p className="text-muted">
              Crea campañas SP con Product Targeting por Categorías y
              Subcategorías
            </p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <FaExclamationTriangle className="me-2" />
            {error}
          </Alert>
        )}

        {result && result.success && !creationComplete && (
          <Alert variant="success" onClose={() => setResult(null)} dismissible>
            <FaCheckCircle className="me-2" />
            {result.message}
          </Alert>
        )}

        {/* Configuration Section */}
        {!result?.amazonResults && (
          <>
            {/* Info Banner */}
            <Alert variant="info" className="mb-4">
              <FaLightbulb className="me-2" />
              <strong>Configuración:</strong> Manual Targeting • Product
              Targeting • Category/Subcategory
            </Alert>

            {/* SITE Selection */}
            <div className="config-section-card mb-4">
              <h5 className="mb-3">
                <Badge bg="primary" className="me-2">
                  1
                </Badge>
                Seleccionar SITE
              </h5>
              <Form.Select
                value={siteType}
                onChange={(e) => setSiteType(e.target.value)}
                className="site-selector"
              >
                <option value="AMAZON_AND_BEYOND">
                  Amazon and Beyond (Multi-Marketplace)
                </option>
                <option value="AMAZON_BUSINESS">
                  Amazon Business (Single Marketplace)
                </option>
              </Form.Select>
            </div>

            {/* Upload Section */}
            <div className="upload-grid">
              {/* Card: Products CSV */}
              <div
                className={`upload-card-new ${productsFile ? "has-file" : ""}`}
              >
                <div className="card-required-badge">Requerido</div>
                <Badge bg="primary" className="step-badge">
                  2
                </Badge>
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">Productos CSV</div>
                <div className="upload-card-desc">
                  Columnas requeridas: ASIN, SKU
                </div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {productsFile ? "Cambiar Archivo" : "Seleccionar Archivo"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleProductsFileChange}
                    hidden
                  />
                </label>
                {productsFile && (
                  <small className="text-success mt-2">
                    {productsFile.name}
                  </small>
                )}
              </div>
            </div>

            {/* Categories Section */}
            <div className="config-section-card mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  <Badge bg="primary" className="me-2">
                    3
                  </Badge>
                  Categorías y Subcategorías
                </h5>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={handleSuggestCategories}
                    disabled={suggestingCategories || !productsFile}
                  >
                    {suggestingCategories ? (
                      <>
                        <Spinner
                          size="sm"
                          animation="border"
                          className="me-1"
                        />
                        Sugiriendo...
                      </>
                    ) : (
                      <>
                        <FaLightbulb className="me-1" />
                        Sugerir Categorías
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={addCategory}
                  >
                    <FaPlus className="me-1" /> Agregar Categoría
                  </Button>
                </div>
              </div>

              {/* Suggested Categories */}
              {suggestedCategories.length > 0 && (
                <div className="suggested-categories mb-3">
                  <small className="text-muted d-block mb-2">
                    Categorías sugeridas (click para agregar):
                  </small>
                  <div className="d-flex flex-wrap gap-2">
                    {suggestedCategories.map((cat, idx) => (
                      <Badge
                        key={idx}
                        bg="secondary"
                        className="suggested-category-badge"
                        onClick={() => selectSuggestedCategory(cat)}
                        style={{ cursor: "pointer" }}
                      >
                        {cat.name || cat.category}
                        {cat.subcategory && ` > ${cat.subcategory}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Category Inputs */}
              {categories.map((cat, index) => (
                <Row key={index} className="mb-3 category-row">
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label className="text-muted small">
                        Categoría
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ej: Electronics, Home & Kitchen..."
                        value={cat.category}
                        onChange={(e) =>
                          handleCategoryChange(
                            index,
                            "category",
                            e.target.value,
                          )
                        }
                        className="category-input"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label className="text-muted small">
                        Subcategoría (Opcional)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ej: Headphones, Kitchen Appliances..."
                        value={cat.subcategory}
                        onChange={(e) =>
                          handleCategoryChange(
                            index,
                            "subcategory",
                            e.target.value,
                          )
                        }
                        className="category-input"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    {categories.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeCategory(index)}
                        className="w-100"
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}
            </div>

            {/* Campaign Configuration */}
            <div className="config-section-card mt-4">
              <h5 className="mb-3">
                <Badge bg="primary" className="me-2">
                  4
                </Badge>
                Configuración de Campaña
              </h5>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Bid Default ($)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0.02"
                      value={defaultBid}
                      onChange={(e) =>
                        setDefaultBid(parseFloat(e.target.value))
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Budget Diario ($)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      min="1"
                      value={defaultBudget}
                      onChange={(e) =>
                        setDefaultBudget(parseFloat(e.target.value))
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Bidding Strategy
                    </Form.Label>
                    <Form.Select
                      value={biddingStrategy}
                      onChange={(e) => setBiddingStrategy(e.target.value)}
                    >
                      <option value="SALES_DOWN_ONLY">
                        Dynamic Bids - Down Only
                      </option>
                      <option value="SALES_UP_AND_DOWN">
                        Dynamic Bids - Up and Down
                      </option>
                      <option value="FIXED_BID">Fixed Bids</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Targeting Type
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value="Manual Targeting"
                      disabled
                      className="bg-dark text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Targeting Strategy
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value="Product Targeting (Category)"
                      disabled
                      className="bg-dark text-success fw-bold"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Generate Button */}
            <div className="generate-section mt-4">
              <button
                className={`btn-generate-campaigns ${!loading && productsFile && categories.some((c) => c.category.trim()) ? "ready" : ""}`}
                onClick={handleGenerateCampaigns}
                disabled={
                  loading ||
                  !productsFile ||
                  !categories.some((c) => c.category.trim())
                }
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Generando campañas de categoría...
                  </>
                ) : (
                  <>
                    <FaRocket />
                    Generar Campañas de Categoría
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* Results Section */}
        {result && campaigns.length > 0 && (
          <Row>
            <Col>
              <Card className="config-section-card border-0 p-0 overflow-hidden">
                <Card.Header className="bg-dark text-white p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>
                      <FaLayerGroup className="me-2" />
                      Campañas de Categoría Generadas ({campaigns.length})
                    </h5>
                  </div>

                  <div>
                    {/* Bulk Actions Row */}
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                      <div className="d-flex gap-2 align-items-center">
                        <span className="text-white fw-bold">
                          Bulk actions:
                        </span>
                        <Form.Select
                          size="sm"
                          value={bulkAction}
                          onChange={(e) => setBulkAction(e.target.value)}
                          style={{ width: "200px" }}
                        >
                          <option value="">Seleccionar acción</option>
                          <option value="budget">Asignar presupuesto...</option>
                          <option value="bid">Asignar bid...</option>
                        </Form.Select>

                        {bulkAction === "budget" && (
                          <Form.Control
                            type="number"
                            size="sm"
                            placeholder="Nuevo presupuesto"
                            value={bulkBudget}
                            onChange={(e) => setBulkBudget(e.target.value)}
                            style={{ width: "150px" }}
                            min="0"
                            step="0.01"
                          />
                        )}

                        {bulkAction === "bid" && (
                          <Form.Control
                            type="number"
                            size="sm"
                            placeholder="Nuevo bid"
                            value={bulkBid}
                            onChange={(e) => setBulkBid(e.target.value)}
                            style={{ width: "150px" }}
                            min="0.02"
                            step="0.01"
                          />
                        )}

                        {bulkAction && (
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={applyBulkAction}
                          >
                            Aplicar
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons Row */}
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Select
                          size="sm"
                          value={campaignState}
                          onChange={(e) => setCampaignState(e.target.value)}
                          style={{ width: "auto" }}
                          disabled={creationComplete}
                        >
                          <option value="ENABLED">Crear como: Enabled</option>
                          <option value="PAUSED">Crear como: Paused</option>
                        </Form.Select>

                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={handleDownloadCampaigns}
                        >
                          <FaDownload className="me-1" /> Descargar CSV
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleReset}
                        >
                          <FaRocket className="me-1" /> Nueva Generación
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body
                  className="p-0 table-responsive bg-dark"
                  style={{ maxHeight: "600px", overflowY: "auto" }}
                >
                  <Table
                    hover
                    variant="dark"
                    className="align-middle mb-0"
                    size="sm"
                    style={{ fontSize: "0.85rem" }}
                  >
                    <thead className="text-warning sticky-top bg-dark">
                      <tr>
                        <th width="40">
                          <Form.Check
                            type="checkbox"
                            checked={campaigns.every((c) => c.selected)}
                            onChange={(e) =>
                              e.target.checked
                                ? selectAllCampaigns()
                                : deselectAllCampaigns()
                            }
                          />
                        </th>
                        <th width="50">#</th>
                        <th width="300">Campaign Name</th>
                        <th width="100">ASIN</th>
                        <th width="150">Category</th>
                        <th width="150">Subcategory</th>
                        <th width="80">Bid</th>
                        <th width="80">Budget</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((campaign, idx) => (
                        <tr key={idx}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={campaign.selected || false}
                              onChange={() => toggleCampaignSelection(idx)}
                            />
                          </td>
                          <td>{idx + 1}</td>
                          <td>
                            <div
                              className="text-truncate"
                              style={{ maxWidth: "280px" }}
                            >
                              {campaign["Campaign Name"]}
                            </div>
                          </td>
                          <td className="text-info">{campaign.ASIN}</td>
                          <td>
                            <Badge bg="primary">{campaign.Category}</Badge>
                          </td>
                          <td>
                            {campaign.Subcategory ? (
                              <Badge bg="secondary">
                                {campaign.Subcategory}
                              </Badge>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>${campaign.Bid}</td>
                          <td>${campaign.Budget}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
                <Card.Footer className="bg-dark border-top border-secondary d-flex justify-content-between p-3">
                  <div className="d-flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={handleReset}
                      className="fw-bold"
                    >
                      ← Cancelar
                    </Button>
                  </div>
                  <div className="d-flex gap-3 align-items-center">
                    <div className="text-muted small">
                      Total: {campaigns.length} | Seleccionadas:{" "}
                      {campaigns.filter((c) => c.selected).length}
                    </div>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleCreateInAmazon}
                      disabled={
                        loading ||
                        creationComplete ||
                        campaigns.filter((c) => c.selected).length === 0
                      }
                      style={{
                        fontWeight: "bold",
                        padding: "6px 20px",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-1"
                          />
                          Creando en Amazon...
                        </>
                      ) : creationComplete ? (
                        <>
                          <FaCheckCircle className="me-1" /> Campañas Creadas
                        </>
                      ) : (
                        <>Crear Campañas de Categoría en Amazon</>
                      )}
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        )}

        {/* Requirements Section */}
        {!result && (
          <div className="requirements-section mt-4">
            <span className="requirements-title">
              📋 Especificaciones Category Campaigns
            </span>
            <div className="req-item">
              <strong>Productos CSV:</strong> Debe contener 'ASIN', 'SKU'.
            </div>
            <div className="req-item">
              <strong>Categorías:</strong> Input manual o sugerencias basadas en
              tus productos.
            </div>
            <div className="req-item">
              <strong>Subcategorías:</strong> Opcional, para targeting más
              específico.
            </div>
            <div className="req-item">
              <strong>Targeting:</strong> Manual Targeting con Product Targeting
              (Category).
            </div>
            <div className="req-item">
              <strong>SITE:</strong> Amazon and Beyond o Amazon Business.
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default SpCategoryCampaigns;
